'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useCart } from '@/contexts/CartContext';

interface FeaturedProduct {
  id: string;
  name: string;
  slug: string;
  price: number;
  images: Array<{ url: string; alternativeText?: string }>;
}

export default function CartSidebar() {
  const { items, isOpen, closeCart, updateQuantity, removeItem, getSubtotal, getDiscount, getTotal, addItem } = useCart();
  const [featuredProduct, setFeaturedProduct] = useState<FeaturedProduct | null>(null);

  // Fetch one random featured product for recommendations
  useEffect(() => {
    if (isOpen && items.length > 0) {
      fetch('/api/strapi/products?populate=*&filters[featured][$eq]=true&pagination[limit]=5')
        .then(res => res.json())
        .then(data => {
          if (data.data && data.data.length > 0) {
            // Pick a random product from the featured list
            const randomIndex = Math.floor(Math.random() * data.data.length);
            const product = data.data[randomIndex];
            setFeaturedProduct({
              id: product.id,
              name: product.name,
              slug: product.slug,
              price: product.price || product.priceFrom || 0,
              images: product.images || []
            });
          }
        })
        .catch(err => console.error('Error fetching featured products:', err));
    }
  }, [isOpen, items.length]);

  if (!isOpen) return null;

  const shipping = 5.0;
  const veterinaryConsultation = 29.9;
  const isFreeShipping = getTotal() >= 50;
  const totalSavings = getDiscount();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-10 z-40"
        onClick={closeCart}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E7E4DD]">
          <h2 className="text-2xl font-bold">Warenkörbchen</h2>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-[#F4F1EB] rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Free Shipping Banner */}
        {isFreeShipping && (
          <div className="bg-[#C8D962] px-6 py-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
            </svg>
            <span className="font-medium">Gratis Versand für dich</span>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 ? (
            <div className="text-center text-[#5F6259] py-12">
              <p>Dein Warenkorb ist leer</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="flex gap-4">
                {/* Product Image */}
                <div className="w-20 h-20 bg-[#F4F1EB] rounded flex-shrink-0">
                  <Image
                    src={item.image}
                    alt={item.name}
                    width={80}
                    height={80}
                    className="object-contain p-2"
                  />
                </div>

                {/* Product Details */}
                <div className="flex-1">
                  <h3 className="font-semibold text-sm mb-1">{item.name}</h3>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border border-[#E7E4DD] rounded">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="px-3 py-1 hover:bg-[#F4F1EB] transition-colors"
                      >
                        -
                      </button>
                      <span className="px-4 py-1 border-x border-[#E7E4DD]">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="px-3 py-1 hover:bg-[#F4F1EB] transition-colors"
                      >
                        +
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <div className="font-semibold">
                        {(item.price * item.quantity).toFixed(2)} €
                      </div>
                    </div>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeItem(item.id)}
                  className="p-1 hover:bg-[#F4F1EB] rounded transition-colors h-fit"
                >
                  <svg className="w-5 h-5 text-[#5F6259]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))
          )}

          {/* Recommended Products */}
          {items.length > 0 && featuredProduct && (
            <div className="border-t border-[#E7E4DD] pt-6">
              <h3 className="font-semibold mb-4">Das könnte dir auch gefallen:</h3>
              <div className="bg-white border border-[#E7E4DD] rounded-lg p-4">
                <div className="flex gap-3 items-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-[#F4F1EB] rounded">
                      <Image
                        src={featuredProduct.images[0]?.url || '/placeholder.svg'}
                        alt={featuredProduct.images[0]?.alternativeText || featuredProduct.name}
                        width={64}
                        height={64}
                        className="object-contain p-1"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">{featuredProduct.name}</h4>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="font-semibold text-[#6B894A]">{featuredProduct.price.toFixed(2)} €</span>
                      </div>
                      <button
                        onClick={() => {
                          addItem({
                            id: featuredProduct.id,
                            name: featuredProduct.name,
                            price: featuredProduct.price,
                            quantity: 1,
                            image: featuredProduct.images[0]?.url || '/placeholder.svg'
                          });
                        }}
                        className="w-8 h-8 bg-[#6B894A] text-white rounded-full flex items-center justify-center hover:bg-[#C8D962] hover:text-black transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer/Summary */}
        {items.length > 0 && (
          <div className="border-t border-[#E7E4DD] p-6 space-y-3">
            <div className="flex justify-between text-sm">
              <span>Versand:</span>
              <span className={isFreeShipping ? 'text-[#6B894A] line-through' : ''}>
                {shipping.toFixed(2)} € {isFreeShipping && <span className="text-[#6B894A] ml-2">Kostenlos</span>}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Tierarztberatung:</span>
              <span className="text-[#6B894A] line-through">
                {veterinaryConsultation.toFixed(2)} € <span className="text-[#6B894A] ml-2">Kostenlos</span>
              </span>
            </div>
            {totalSavings > 0 && (
              <div className="flex justify-between text-sm text-[#6B894A]">
                <span>Ersparnis:</span>
                <span>{totalSavings.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-3 border-t border-[#E7E4DD]">
              <span>Summe:</span>
              <div>
                {totalSavings > 0 && (
                  <span className="text-sm text-[#5F6259] line-through mr-2">
                    {(getSubtotal() + (isFreeShipping ? 0 : shipping)).toFixed(2)} €
                  </span>
                )}
                <span className="text-[#BA2F27]">{(getTotal() + (isFreeShipping ? 0 : shipping)).toFixed(2)} €</span>
              </div>
            </div>

            <Link
              href="/de/checkout"
              className="block w-full bg-[#73140D] text-white text-center py-3 rounded-lg font-medium hover:bg-[#BA2F27] transition-colors"
              onClick={closeCart}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                SICHER BEZAHLEN
              </span>
            </Link>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-[#BA2F27] mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="italic">100% Zufriedenheitsgarantie</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Image src="/placeholder.svg" alt="PayPal" width={40} height={25} />
                <Image src="/placeholder.svg" alt="Visa" width={40} height={25} />
                <Image src="/placeholder.svg" alt="Mastercard" width={40} height={25} />
                <Image src="/placeholder.svg" alt="Amex" width={40} height={25} />
                <Image src="/placeholder.svg" alt="Google Pay" width={40} height={25} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
